/*
 * Planigo
 * Auteur  : Stéphane Raverdy
 * Société : SCIAD 2026
 */
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsApi, searchApi, adminApi, profileApi, googleCalendarApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import {
  Folders, House, ListChecks, Clock, TestTube, GearSix, Bell, SignOut, Buildings,
  CaretDown, Lightning, Target, ArrowRight, CheckCircle, Info, MagnifyingGlass, User, Briefcase, FileText, ChartBar, Receipt, Wrench, Money, Gauge, CalendarX, SidebarSimple, List, Camera, Key, Trash, QrCode, Package
} from '@phosphor-icons/react';
import { getInitials, roleLabels } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { useAppConfig } from '../contexts/ConfigContext';
import { toast } from 'sonner';
import useMobileWorkspace from '../hooks/useMobileWorkspace';

// REGLE DE GESTION : le droit menu:* pilote la presence du menu et l acces a la route.
// Il complete les permissions metier (read/create/update/...) sans les remplacer.
const MENU_PERMISSION_BY_PATH = {
  '/':'menu:dashboard', '/projects':'menu:projects', '/tasks':'menu:tasks', '/workload':'menu:workload',
  '/absences':'menu:absences', '/requirements':'menu:requirements', '/time':'menu:time', '/tests':'menu:tests',
  '/sprints':'menu:sprints', '/governance':'menu:governance', '/bi':'menu:bi', '/settings':'menu:settings',
  '/invoicing':'menu:invoicing', '/expenses':'menu:expenses', '/validations':'menu:validations', '/admin':'menu:admin',
  '/about':'menu:about', '/technical':'menu:technical', '/interventions':'menu:interventions', '/assets':'menu:assets',
  '/gmao':'menu:assets','/suppliers':'menu:assets','/sav':'menu:assets','/loans':'menu:assets','/audits':'menu:assets','/store':'menu:assets','/inventory':'menu:assets','/purchase-orders':'menu:assets','/supplier-invoices':'menu:assets','/maintenance-planning':'menu:assets','/gmao-reporting':'menu:assets',
};

const navItems = [
  { path: '/', label: 'Tableau de Bord', icon: House },
  { path: '/projects', label: 'Projets', icon: Folders, perm: 'projects:read' },
  { path: '/gmao', label: 'Cockpit GMAO', icon: Gauge, perm: 'assets:read' },
  { path: '/interventions', label: 'Interventions', icon: Wrench, perm: 'interventions:read' },
  { path: '/assets', label: 'Parc & stocks', icon: Briefcase, perm: 'assets:read' },
  { path: '/suppliers', label: 'Fournisseurs & catalogues', icon: Buildings, perm: 'assets:read' },
  { path: '/sav', label: 'SAV', icon: Wrench, perm: 'assets:read' },
  { path: '/inventory', label: 'Inventaires / contrôles', icon: ListChecks, perm: 'assets:read' },
  { path: '/purchase-orders', label: 'Commandes', icon: Receipt, perm: 'assets:read' },
  { path: '/supplier-invoices', label: 'Factures fournisseurs', icon: FileText, perm: 'assets:read' },
  { path: '/maintenance-planning', label: 'Planning', icon: Clock, perm: 'interventions:read' },
  { path: '/gmao-reporting', label: 'Reporting', icon: ChartBar, perm: 'assets:read' },
  { path: '/tasks', label: 'Taches & GANTT', icon: Target, perm: 'tasks:read' },
  { path: '/workload', label: 'Plan de charge', icon: Gauge, perm: 'workload:read' },
  { path: '/absences', label: 'Absences', icon: CalendarX, perm: 'absences:read' },
  { path: '/requirements', label: 'Exigences', icon: ListChecks, perm: 'requirements:read' },
  { path: '/time', label: 'Suivi Temps', icon: Clock, perm: 'time:read' },
  { path: '/tests', label: 'Tests & QA', icon: TestTube, perm: 'tests:read' },
  { path: '/sprints', label: 'Sprints & Jalons', icon: Lightning, perm: 'sprints:read' },
  { path: '/governance', label: 'Gouvernance', icon: ListChecks, perm: 'governance:read' },
  { path: '/bi', label: 'Statistiques BI', icon: ChartBar, perm: 'bi:read' },
  { path: '/technical', label: 'Simulateur appareils', icon: GearSix, perm: 'technical:simulator' },
];

const adminNavItems = [
  { path: '/settings', label: 'Parametrage', icon: Wrench, perm: 'admin:settings' },
  { path: '/invoicing', label: 'Facturation', icon: Receipt, perm: 'invoicing:read' },
  { path: '/expenses', label: 'Notes de frais', icon: Money, perm: 'expenses:read' },
  { path: '/validations', label: 'Suivi des CRA', icon: CheckCircle, perm: 'cra:read' },
];

const superAdminNavItems = [
  { path: '/admin', label: 'Administration', icon: GearSix, perm: 'admin:users:read' },
];

const MENU_ICONS = { house:House, folders:Folders, target:Target, gauge:Gauge, calendarx:CalendarX, listchecks:ListChecks, clock:Clock, testtube:TestTube, lightning:Lightning, chartbar:ChartBar, wrench:Wrench, receipt:Receipt, money:Money, checkcircle:CheckCircle, gear:GearSix, bell:Bell, briefcase:Briefcase, buildings:Buildings, package:Package };

// REGLE DE GESTION V5.1 : le menu est regroupe par univers fonctionnel.
// Le regroupement ne change jamais les permissions ni l'ordre configure a l'interieur du groupe.
const MENU_GROUP_BY_PATH = {
  // REGLE DE GESTION V5.3 : le Tableau de bord reste seul en tete.
  '/':'dashboard',
  '/projects':'projects','/tasks':'projects','/workload':'projects','/bi':'projects',
  // Exigences et gouvernance sont regroupes pour reduire la profondeur du menu.
  '/requirements':'governance','/governance':'governance','/tests':'governance','/sprints':'governance',
  // Le suivi du temps contient aussi les absences, CRA et frais associes.
  '/time':'time','/absences':'time','/expenses':'time','/validations':'time',
  // REGLE DE GESTION V5.4 : Parc, terrain, magasin et facturation restent dans le meme univers IT.
  '/gmao':'it','/interventions':'it','/assets':'it','/suppliers':'it','/sav':'it','/loans':'it','/audits':'it','/store':'it','/inventory':'it','/purchase-orders':'it','/supplier-invoices':'it','/maintenance-planning':'it','/gmao-reporting':'it','/invoicing':'it',
  '/technical':'technical',
  '/settings':'admin','/admin':'admin',
};
const MENU_GROUP_LABELS = { projects:'Projets', governance:'Gouvernance', time:'Suivi du temps', it:'Parc & Interventions', technical:'Technique' };
const MENU_GROUP_ICONS = { projects: Folders, governance: ListChecks, time: Clock, it: Wrench, technical: GearSix, admin: GearSix };

export default function MainLayout({ children }) {
  const { user, logout, isAdmin, isSupervisor, hasPermission, checkAuth } = useAuth();
  const mobileWorkspace = useMobileWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme, changeTheme, themes } = useTheme();
  const { menus: configuredMenus } = useAppConfig();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [smallLogoUrl, setSmallLogoUrl] = useState(null);
  const [logoSettings, setLogoSettings] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('projethub-sidebar-collapsed') === '1');
  // REGLE DE GESTION V6.0.4 : les univers du menu gauche sont de vrais accordéons.
  const [openMenuGroups, setOpenMenuGroups] = useState({ projects: true, governance: false, time: false, it: false, technical: false, admin: false });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuGroup, setMobileMenuGroup] = useState(null);
  const [enabledMenus, setEnabledMenus] = useState({});
  const [isDemoMode, setIsDemoMode] = useState(false);
  const searchRef = useRef(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileTab, setProfileTab] = useState('profile');
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profilePasswords, setProfilePasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [googleStatus, setGoogleStatus] = useState({ configured: false, linked: false });
  const [googleBusy, setGoogleBusy] = useState(false);
  const avatarInputRef = useRef(null);

  useEffect(() => { setProfileName(user?.name || ''); }, [user]);

  // REGLE DE GESTION : un mot de passe temporaire envoye par l'administrateur doit etre change a la premiere connexion.
  useEffect(() => {
    if (user?.force_password_change) {
      setProfileTab('password');
      setProfileOpen(true);
    }
  }, [user?.force_password_change]);

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      await profileApi.update({ name: profileName });
      await checkAuth();
      toast.success('Profil mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de mise à jour du profil'); }
    finally { setProfileSaving(false); }
  };

  const savePassword = async () => {
    if (!profilePasswords.new_password || profilePasswords.new_password.length < 6) return toast.error('6 caractères minimum');
    if (profilePasswords.new_password !== profilePasswords.confirm_password) return toast.error('Les mots de passe ne correspondent pas');
    setProfileSaving(true);
    try {
      await profileApi.changePassword({ current_password: profilePasswords.current_password, new_password: profilePasswords.new_password });
      setProfilePasswords({ current_password: '', new_password: '', confirm_password: '' });
      await checkAuth();
      setProfileOpen(false);
      toast.success('Mot de passe modifié');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de modification du mot de passe'); }
    finally { setProfileSaving(false); }
  };

  const uploadAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProfileSaving(true);
    try {
      await profileApi.uploadAvatar(file);
      await checkAuth();
      toast.success('Avatar mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur avatar'); }
    finally { setProfileSaving(false); event.target.value = ''; }
  };

  const removeAvatar = async () => {
    setProfileSaving(true);
    try { await profileApi.deleteAvatar(); await checkAuth(); toast.success('Avatar supprimé'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur avatar'); }
    finally { setProfileSaving(false); }
  };

  const loadGoogleStatus = async () => {
    try { const r = await googleCalendarApi.status(); const st=r.data || { enabled:false, configured:false, linked:false }; setGoogleStatus(st); if (!st.enabled && profileTab==='google') setProfileTab('profile'); }
    catch { setGoogleStatus({ enabled:false, configured:false, linked:false }); if (profileTab==='google') setProfileTab('profile'); }
  };

  const connectGoogleCalendar = async () => {
    setGoogleBusy(true);
    try {
      const r = await googleCalendarApi.connect();
      const url = r.data?.authorization_url;
      if (!url) throw new Error('URL Google absente');
      window.open(url, 'projethub-google-calendar', 'width=620,height=760,resizable=yes,scrollbars=yes');
    } catch (e) { toast.error(e.response?.data?.detail || 'Impossible de rattacher Google Agenda'); }
    finally { setGoogleBusy(false); }
  };

  const disconnectGoogleCalendar = async () => {
    if (!window.confirm('Déconnecter Google Agenda de votre compte Planigo ?')) return;
    setGoogleBusy(true);
    try { await googleCalendarApi.disconnect(); await loadGoogleStatus(); toast.success('Google Agenda déconnecté'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur de déconnexion Google'); }
    finally { setGoogleBusy(false); }
  };

  useEffect(() => {
    if (profileOpen) loadGoogleStatus();
  }, [profileOpen, profileTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // REGLE DE GESTION : le popup OAuth ne transmet aucun token au frontend.
    // Il signale uniquement que le rattachement serveur est terminé.
    const handler = (event) => {
      const expectedOrigin = (() => {
        try { return new URL(process.env.REACT_APP_BACKEND_URL || window.location.origin, window.location.origin).origin; }
        catch { return window.location.origin; }
      })();
      if (event.origin !== expectedOrigin || event.data?.type !== 'projethub-google-linked') return;
      loadGoogleStatus();
      if (event.data?.ok) toast.success('Google Agenda rattaché');
      else toast.error('Le rattachement Google Agenda a échoué');
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // V3: menu labels, order, icons and activation are configuration-driven.
  // Permissions remain the security boundary; menu configuration only controls presentation.
  const fallbackNavItems = [...navItems, ...adminNavItems, ...superAdminNavItems].map(item => ({...item, menuPerm: MENU_PERMISSION_BY_PATH[item.path]}));
  const allNavItems = (configuredMenus?.length ? configuredMenus.map(m => ({
    path: m.path,
    label: m.label,
    icon: MENU_ICONS[m.icon] || Target,
    perm: m.permission,
    menuPerm: m.menu_permission || MENU_PERMISSION_BY_PATH[m.path],
    order: Number(m.order || 0),
    enabled: m.enabled !== false,
  })) : fallbackNavItems).sort((a,b)=>(a.order||0)-(b.order||0));

  const clientAllowedPaths = new Set(['/notifications']);
  // V6.0.4 : un role peut exposer des menus differents sur desktop et mobile/tablette.
  // L'absence de configuration conserve le comportement historique (tous les menus autorises par permissions).
  const roleDevicePaths = mobileWorkspace.deviceClass === 'mobile' ? user?.mobile_menu_paths : mobileWorkspace.deviceClass === 'tablet' ? user?.tablet_menu_paths : user?.desktop_menu_paths;
  const visibleNavItems = allNavItems.reduce((acc, item) => {
    // REGLE DE GESTION V6.3.1 : Prêts et Audits restent accessibles dans le domaine Parc mais ne sont plus des raccourcis du menu gauche.
    if (item.path === '/loans' || item.path === '/audits') return acc;
    if (Array.isArray(roleDevicePaths) && !roleDevicePaths.includes(item.path) && item.path !== '/') return acc;
    if ((user?.role === 'client' || user?.user_type === 'internal_client') && item.path !== '/' && !clientAllowedPaths.has(item.path)) return acc;
    if (item.menuPerm && !hasPermission(item.menuPerm)) return acc;
    if (item.path === '/settings') {
      const canOpenSettings = ['settings:clients','settings:providers','settings:services','settings:teams','settings:absences','settings:users','settings:referentials','settings:notifications','settings:general'].some(hasPermission);
      if (!canOpenSettings) return acc;
    } else if (item.perm && !hasPermission(item.perm)) return acc;
    if (item.enabled === false || enabledMenus[item.path] === false) return acc;
    acc.push(item);
    return acc;
  }, []);
  const currentPage = visibleNavItems.find(item => item.path === location.pathname)?.label || 'Planigo';
  const groupedNavItems = {
    dashboard: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'dashboard'),
    projects: visibleNavItems.filter(item => (MENU_GROUP_BY_PATH[item.path] || 'projects') === 'projects'),
    governance: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'governance'),
    time: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'time'),
    it: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'it'),
    technical: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'technical'),
    admin: visibleNavItems.filter(item => MENU_GROUP_BY_PATH[item.path] === 'admin'),
  };
  const activeDesktopGroup = ['projects','governance','time','it','technical'].find(group =>
    groupedNavItems[group].some(item => location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)))
  );
  useEffect(() => {
    if (activeDesktopGroup) setOpenMenuGroups(prev => prev[activeDesktopGroup] ? prev : ({...prev, [activeDesktopGroup]: true}));
  }, [activeDesktopGroup]);
  const toggleDesktopGroup = (group) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      localStorage.setItem('projethub-sidebar-collapsed', '0');
      setOpenMenuGroups(prev => ({...prev, [group]: true}));
      return;
    }
    setOpenMenuGroups(prev => ({...prev, [group]: !prev[group]}));
  };

  // REGLE DE GESTION V5.4.0.2 : sur mobile, on navigue par petits univers
  // plutôt qu'en ouvrant la grande sidebar desktop.
  const mobileMenuGroups = [
    { key: 'projects', label: 'Projets', icon: Folders, items: groupedNavItems.projects },
    { key: 'governance', label: 'Gouvernance', icon: ListChecks, items: groupedNavItems.governance },
    { key: 'time', label: 'Suivi du temps', icon: Clock, items: groupedNavItems.time },
    { key: 'it', label: 'Parc & Interventions', icon: Wrench, items: groupedNavItems.it },
    { key: 'technical', label: 'Technique', icon: GearSix, items: groupedNavItems.technical },
  ].filter(group => group.items.length > 0);

  const openMobileMenu = () => { setMobileSidebarOpen(false); setMobileMenuGroup(null); setMobileMenuOpen(true); };

  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // REGLE DE GESTION V6.1.0 : le branding est public et valide cote serveur. Un utilisateur sans droit
    // Administration doit quand meme voir le logo configure. Si le logo est absent,
    // invalide ou inaccessible, l'identite Planigo integree est utilisee.
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/logo`)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('branding unavailable')))
      .then(data => {
        setLogoUrl(data?.logo_url || null);
        setSmallLogoUrl(data?.logo_app_url || null);
        setLogoSettings({
          sidebar_height: data?.logo_sidebar_height || 38,
          sidebar_width: data?.logo_sidebar_width || 170,
        });
      })
      .catch(() => { setLogoUrl(null); setSmallLogoUrl(null); });

    adminApi.getSettings().then(res => {
      if (res.data?.enabled_menus) setEnabledMenus(res.data.enabled_menus);
    }).catch(() => {});
    adminApi.getDemoStatus().then(res => {
      setIsDemoMode(res.data?.demo_mode || false);
    }).catch(() => {});
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('projethub-sidebar-collapsed', next ? '1' : '0');
      return next;
    });
  };

  useEffect(() => { setMobileSidebarOpen(false); setMobileMenuOpen(false); setMobileMenuGroup(null); }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const [notifsRes, countRes] = await Promise.all([
        notificationsApi.getAll(),
        notificationsApi.getCount(),
      ]);
      setNotifications(notifsRes.data.slice(0, 15));
      setUnreadCount(countRes.data.count);
    } catch {}
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) {
      try { await notificationsApi.markRead(notif.id); } catch {}
    }
    setNotifOpen(false);
    if (notif.link) navigate(notif.link);
    fetchNotifications();
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      fetchNotifications();
    } catch {}
  };

  // Search
  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await searchApi.search(searchQuery);
        setSearchResults(res.data);
        setSearchOpen(true);
      } catch { setSearchResults([]); }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const handleClick = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setSearchOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const typeIcons = { project: Folders, task: Target, client: Briefcase, user: User, requirement: FileText, test: TestTube };
  const typeLabels = { project: 'Projet', task: 'Tache', client: 'Client', user: 'Utilisateur', requirement: 'Exigence', test: 'Test' };
  const typeRoutes = { project: '/projects', task: '/tasks', client: '/admin', user: '/admin', requirement: '/requirements', test: '/tests' };

  const handleSearchResultClick = (r) => {
    setSearchOpen(false);
    setSearchQuery('');
    navigate(typeRoutes[r.type] || '/');
  };

  const isClientUser = user?.role === 'client' || user?.user_type === 'internal_client';
  if (isClientUser) {
    return (
      <div className="client-shell" data-testid="client-layout">
        <header className="client-topbar">
          <Link to="/" className="client-brand">
            <img
              src={(smallLogoUrl || logoUrl) ? `${process.env.REACT_APP_BACKEND_URL}${smallLogoUrl || logoUrl}` : '/planigo-logo.png'}
              onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src='/planigo-logo.png';}}
              alt="Planigo" className="h-8 max-w-[150px] w-auto object-contain"
            />
            <div><strong>Espace Client</strong><span>Suivi de vos projets</span></div>
          </Link>
          <nav className="client-nav">
            <Link to="/" className={location.pathname==='/'?'active':''}><House size={17}/>Tableau de bord</Link>
            <Link to="/notifications" className={location.pathname.startsWith('/notifications')?'active':''}><Bell size={17}/>Notifications{unreadCount>0&&<em>{unreadCount>9?'9+':unreadCount}</em>}</Link>
          </nav>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" className="client-profile-btn"><Avatar className="w-8 h-8"><AvatarFallback className="text-[10px] text-white" style={{backgroundColor:user?.avatar_color||'#2563eb'}}>{getInitials(user?.name||'')}</AvatarFallback></Avatar><span className="hidden sm:block">Mon espace</span><CaretDown size={11}/></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52"><DropdownMenuItem onClick={()=>{setProfileTab('password');setProfileOpen(true)}}><Key size={14} className="mr-2"/>Mot de passe</DropdownMenuItem><DropdownMenuItem onClick={handleLogout} className="text-red-600"><SignOut size={14} className="mr-2"/>Déconnexion</DropdownMenuItem></DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="client-content">{children}</main>
        <nav className="client-mobile-nav" aria-label="Navigation client mobile"><Link to="/" className={location.pathname==='/'?'active':''}><House size={19}/><span>Accueil</span></Link><Link to="/notifications" className={location.pathname.startsWith('/notifications')?'active':''}><Bell size={19}/><span>Notifications</span>{unreadCount>0&&<em>{unreadCount>9?'9+':unreadCount}</em>}</Link><button type="button" onClick={()=>{setProfileTab('password');setProfileOpen(true)}}><User size={19}/><span>Compte</span></button></nav>
        <Dialog open={profileOpen} onOpenChange={setProfileOpen}><DialogContent className="sm:max-w-[520px]"><DialogHeader><DialogTitle>Sécurité du compte</DialogTitle></DialogHeader><div className="space-y-4"><div><Label>Mot de passe actuel</Label><Input type="password" value={profilePasswords.current_password} onChange={e=>setProfilePasswords(q=>({...q,current_password:e.target.value}))}/></div><div><Label>Nouveau mot de passe</Label><Input type="password" value={profilePasswords.new_password} onChange={e=>setProfilePasswords(q=>({...q,new_password:e.target.value}))}/></div><div><Label>Confirmer</Label><Input type="password" value={profilePasswords.confirm_password} onChange={e=>setProfilePasswords(q=>({...q,confirm_password:e.target.value}))}/></div><div className="flex justify-end"><Button onClick={savePassword} disabled={profileSaving}>Modifier</Button></div></div></DialogContent></Dialog>
      </div>
    );
  }

  return (
    <div className="app-shell" data-testid="main-layout">
      {mobileSidebarOpen && <button className="sidebar-overlay" aria-label="Fermer le menu" onClick={() => setMobileSidebarOpen(false)} />}
      {/* Sidebar */}
      <div
        className={`sidebar app-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileSidebarOpen ? 'mobile-open' : ''}`}
        data-testid="sidebar"
      >
        <div className="sidebar-brand">
          <div className="planigo-sidebar-fallback">
            <img
              src={sidebarCollapsed
                ? (smallLogoUrl ? `${process.env.REACT_APP_BACKEND_URL}${smallLogoUrl}` : '/planigo-mark.png')
                : (logoUrl ? `${process.env.REACT_APP_BACKEND_URL}${logoUrl}` : '/planigo-logo.png')}
              onError={(e)=>{e.currentTarget.onerror=null;e.currentTarget.src=sidebarCollapsed?'/planigo-mark.png':'/planigo-logo.png';}}
              alt="Planigo"
              style={sidebarCollapsed ? {maxWidth:'38px',maxHeight:'38px'} : {height:`${logoSettings.sidebar_height || 38}px`,maxWidth:`${logoSettings.sidebar_width || 170}px`,width:'auto'}}
              className="object-contain"
              data-testid="sidebar-logo"
            />
          </div>
        </div>

        <nav className="py-2 px-2 space-y-0.5 flex flex-col flex-1">
          {groupedNavItems.dashboard.map(item => {
            const Icon=item.icon; const isActive=location.pathname===item.path;
            return <Link key={item.path} to={item.path} className={`sidebar-link sidebar-dashboard-link ${isActive?'active':''}`} data-testid="nav-home" title={sidebarCollapsed?item.label:undefined}><Icon size={16} weight={isActive?'fill':'regular'}/><span className="sidebar-label text-[13px]">{item.label}</span></Link>;
          })}
          {['projects','governance','time','it','technical'].map(group => {
            if (!groupedNavItems[group].length) return null;
            const GroupIcon = MENU_GROUP_ICONS[group] || Folders;
            const groupActive = activeDesktopGroup === group;
            const groupOpen = !!openMenuGroups[group];
            return <div key={group} className={`sidebar-accordion sidebar-accordion-${group} ${groupActive ? 'active' : ''}`}>
              <button type="button" className={`sidebar-group-head ${groupActive ? 'active' : ''}`} onClick={() => toggleDesktopGroup(group)} title={sidebarCollapsed ? MENU_GROUP_LABELS[group] : undefined}>
                <GroupIcon size={17} weight={groupActive ? 'fill' : 'regular'} />
                <span className="sidebar-label text-[13px]">{MENU_GROUP_LABELS[group]}</span>
                {!sidebarCollapsed && <CaretDown size={12} className={`sidebar-group-caret ${groupOpen ? 'open' : ''}`} />}
              </button>
              {!sidebarCollapsed && groupOpen && <div className="sidebar-submenu">
                {groupedNavItems[group].map(item => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
                  return <Link key={item.path} to={item.path} className={`sidebar-sub-link ${isActive ? 'active' : ''}`} data-testid={`nav-${item.path.replace('/', '') || 'home'}`}>
                    <Icon size={14} weight={isActive ? 'fill' : 'regular'} />
                    <span>{item.label}</span>
                  </Link>;
                })}
              </div>}
            </div>;
          })}
          <div className="mt-auto pt-4 border-t border-white/10">
            <button type="button" className="sidebar-link sidebar-toggle-bottom w-full" onClick={toggleSidebar} title={sidebarCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}>
              <SidebarSimple size={15} />
              <span className="sidebar-label text-[13px]">{sidebarCollapsed ? 'Agrandir' : 'Réduire le menu'}</span>
            </button>
          </div>
        </nav>
      </div>

      {/* Main content */}
      <div className={`app-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="topbar h-11 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="mobile-sidebar-btn h-7 w-7" onClick={openMobileMenu}><List size={18}/></Button>
            <span className="text-sm font-semibold app-title">{currentPage}</span>
            {isDemoMode && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 animate-pulse" data-testid="demo-mode-badge">
                MODE DEMO
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Global Search */}
            <div className="relative" ref={searchRef}>
              <div className="relative">
                <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => searchResults.length > 0 && setSearchOpen(true)}
                  className="h-7 w-52 pl-7 text-xs bg-slate-100 border-0 focus:bg-white focus:ring-1"
                  data-testid="global-search-input"
                />
              </div>
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute top-full mt-1 right-0 w-80 bg-white rounded-lg shadow-xl border z-50 max-h-72 overflow-y-auto" data-testid="search-results">
                  {searchResults.map((r, i) => {
                    const Icon = typeIcons[r.type] || Target;
                    return (
                      <div key={i} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b last:border-0 transition-colors"
                        onClick={() => handleSearchResultClick(r)} data-testid={`search-result-${i}`}>
                        <div className="w-7 h-7 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Icon size={14} className="text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-900 truncate">{r.title}</p>
                          <p className="text-[10px] text-slate-500 truncate">{typeLabels[r.type]} {r.subtitle ? `- ${r.subtitle}` : ''}</p>
                        </div>
                        {r.status && <Badge className="text-[9px] bg-slate-100 text-slate-600 ml-1">{r.status}</Badge>}
                      </div>
                    );
                  })}
                </div>
              )}
              {searchOpen && searchQuery.length >= 2 && searchResults.length === 0 && (
                <div className="absolute top-full mt-1 right-0 w-64 bg-white rounded-lg shadow-xl border z-50 p-4 text-center text-xs text-slate-500">
                  Aucun resultat pour "{searchQuery}"
                </div>
              )}
            </div>
            {/* Notifications */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-7 w-7" data-testid="notifications-btn">
                  <Bell size={15} className="text-slate-500" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <span className="text-sm font-semibold">Notifications</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={handleMarkAllRead} data-testid="mark-all-read-btn">
                      <CheckCircle size={12} className="mr-1" />Tout lire
                    </Button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <p className="text-center text-slate-500 text-sm py-6">Aucune notification</p>
                  ) : (
                    notifications.map(notif => (
                      <div key={notif.id}
                        className={`px-3 py-2 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${!notif.read ? 'bg-blue-50/50' : ''}`}
                        onClick={() => handleNotifClick(notif)}
                        data-testid={`notif-${notif.id}`}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-medium ${!notif.read ? 'text-slate-900' : 'text-slate-600'}`}>{notif.title}</p>
                            <p className="text-[10px] text-slate-500 line-clamp-2">{notif.message}</p>
                            {notif.actor_name && <p className="text-[9px] text-slate-400 mt-0.5">par {notif.actor_name}</p>}
                            {notif.link && (
                              <span className="text-[10px] text-blue-600 flex items-center gap-0.5 mt-0.5">
                                Ouvrir le projet <ArrowRight size={8} />
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t"><Button variant="ghost" className="w-full text-xs" onClick={()=>{setNotifOpen(false);navigate('/notifications')}}>Ouvrir le centre de notifications <ArrowRight size={12} className="ml-1"/></Button></div>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 h-9 px-2 rounded-xl" data-testid="user-menu-btn">
                  <Avatar className="w-7 h-7 border border-slate-200">
                    {user?.avatar_url && <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt={user?.name || 'Avatar'} />}
                    <AvatarFallback className="text-[10px] text-white" style={{ backgroundColor: user?.avatar_color || '#2563EB' }}>{getInitials(user?.name || '')}</AvatarFallback>
                  </Avatar>
                  <div className="hidden md:block text-left leading-tight">
                    <div className="text-xs font-semibold text-slate-700">{user?.name}</div>
                    <div className="text-[9px] text-slate-400">{roleLabels[user?.role] || user?.role || 'Utilisateur'}{(user?.additional_roles||[]).length ? ` +${user.additional_roles.length}` : ''}</div>
                  </div>
                  <CaretDown size={10} className="text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => { setProfileTab('profile'); setProfileOpen(true); }} className="text-xs">
                  <User size={14} className="mr-2" />Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setProfileTab('password'); setProfileOpen(true); }} className="text-xs">
                  <Key size={14} className="mr-2" />Mot de passe
                </DropdownMenuItem>
                {(groupedNavItems.admin.length > 0 || (hasPermission('menu:about') && (!Array.isArray(roleDevicePaths) || roleDevicePaths.includes('/about')))) && <div className="my-1 border-t" />}
                {groupedNavItems.admin.map(item => {
                  const Icon = item.icon;
                  return <DropdownMenuItem key={item.path} onClick={() => navigate(item.path)} className="text-xs" data-testid={`user-nav-${item.path.replace('/', '')}`}>
                    <Icon size={14} className="mr-2" />{item.label}
                  </DropdownMenuItem>;
                })}
                {hasPermission('menu:about') && (!Array.isArray(roleDevicePaths) || roleDevicePaths.includes('/about')) && <DropdownMenuItem onClick={() => navigate('/about')} className="text-xs" data-testid="user-nav-about">
                  <Info size={14} className="mr-2" />À propos
                </DropdownMenuItem>}
                <div className="px-2 py-2 border-y mt-1">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Theme</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(themes).map(([key, t]) => (
                      <button key={key} onClick={() => changeTheme(key)} className={`w-7 h-7 rounded-full border-2 transition-all ${currentTheme === key ? 'border-slate-900 scale-110 ring-1 ring-offset-1 ring-slate-400' : 'border-transparent hover:scale-105'}`} style={{ background: t.swatch || t.sidebarFrom }} title={t.name} data-testid={`theme-${key}`} />
                    ))}
                  </div>
                </div>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 text-xs mt-1" data-testid="logout-btn">
                  <SignOut size={13} className="mr-1.5" />Deconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
          <DialogContent className="sm:max-w-[680px]">
            <DialogHeader><DialogTitle>Mon profil</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-[180px_1fr] gap-5 pt-2">
              <div className="rounded-2xl border bg-slate-50 p-4 flex flex-col items-center gap-3">
                <Avatar className="w-24 h-24 border-4 border-white shadow">
                  {user?.avatar_url && <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`} alt={user?.name || 'Avatar'} />}
                  <AvatarFallback className="text-2xl text-white" style={{ backgroundColor: user?.avatar_color || '#2563EB' }}>{getInitials(user?.name || '')}</AvatarFallback>
                </Avatar>
                <input ref={avatarInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={uploadAvatar} />
                <Button size="sm" variant="outline" className="w-full" onClick={() => avatarInputRef.current?.click()} disabled={profileSaving}><Camera size={14} className="mr-1"/>Photo / avatar</Button>
                {user?.avatar_url && <Button size="sm" variant="ghost" className="w-full text-red-600" onClick={removeAvatar} disabled={profileSaving}><Trash size={14} className="mr-1"/>Supprimer</Button>}
                <div className="text-center"><div className="text-sm font-bold">{user?.name}</div><div className="text-[11px] text-slate-500">{user?.email}</div></div>
              </div>
              <div>
                <div className="flex gap-2 mb-4">
                  <Button size="sm" variant={profileTab==='profile'?'default':'outline'} onClick={()=>setProfileTab('profile')}>Profil</Button>
                  <Button size="sm" variant={profileTab==='password'?'default':'outline'} onClick={()=>setProfileTab('password')}>Mot de passe</Button>
                  {googleStatus.enabled && <Button size="sm" variant={profileTab==='google'?'default':'outline'} onClick={()=>setProfileTab('google')}>Google Agenda</Button>}
                </div>
                {profileTab==='profile' ? <div className="space-y-4">
                  <div><Label>Nom / Prénom</Label><Input value={profileName} onChange={e=>setProfileName(e.target.value)} /></div>
                  <div><Label>Email</Label><Input value={user?.email || ''} disabled /></div>
                  <div className="rounded-lg bg-blue-50 text-blue-800 p-3 text-xs">Le rôle, le prestataire, les droits et les paramètres administratifs restent gérés par l'administrateur.</div>
                  <div className="flex justify-end"><Button onClick={saveProfile} disabled={profileSaving || !profileName.trim()}>Enregistrer</Button></div>
                </div> : profileTab==='password' ? <div className="space-y-4">
                  <div><Label>Mot de passe actuel</Label><Input type="password" value={profilePasswords.current_password} onChange={e=>setProfilePasswords(p=>({...p,current_password:e.target.value}))}/></div>
                  <div><Label>Nouveau mot de passe</Label><Input type="password" value={profilePasswords.new_password} onChange={e=>setProfilePasswords(p=>({...p,new_password:e.target.value}))}/></div>
                  <div><Label>Confirmer</Label><Input type="password" value={profilePasswords.confirm_password} onChange={e=>setProfilePasswords(p=>({...p,confirm_password:e.target.value}))}/></div>
                  <div className="flex justify-end"><Button onClick={savePassword} disabled={profileSaving}>Modifier le mot de passe</Button></div>
                </div> : <div className="space-y-4">
                  <div className="rounded-xl border bg-slate-50 p-4 space-y-2">
                    <div className="flex items-center justify-between gap-3"><div><div className="font-semibold text-sm">Google Agenda</div><div className="text-[11px] text-slate-500">Compte Planigo autorisé : {user?.email}</div></div><Badge className={googleStatus.linked?'bg-emerald-100 text-emerald-700':'bg-slate-100 text-slate-600'}>{googleStatus.linked?'Rattaché':'Non rattaché'}</Badge></div>
                    <p className="text-xs text-slate-600">La connexion Planigo actuelle reste obligatoire. Le compte Google sert uniquement à synchroniser l'agenda et doit utiliser exactement la même adresse e-mail.</p>
                    {googleStatus.linked && <div className="text-xs"><b>Adresse Google :</b> {googleStatus.google_email || '-'}</div>}
                    {googleStatus.last_sync_at && <div className="text-[10px] text-slate-500">Dernière synchronisation : {new Date(googleStatus.last_sync_at).toLocaleString('fr-FR')}</div>}
                  </div>
                  {!googleStatus.configured ? <div className="rounded-lg bg-amber-50 text-amber-900 p-3 text-xs">L'administrateur doit encore renseigner les identifiants OAuth Google de Planigo.</div> : googleStatus.linked ? <Button variant="outline" className="text-red-600" onClick={disconnectGoogleCalendar} disabled={googleBusy}>Déconnecter Google Agenda</Button> : <Button onClick={connectGoogleCalendar} disabled={googleBusy}>Identifier mon adresse Google</Button>}
                </div>}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="app-content min-h-[calc(100vh-44px)]">
          {children}
        </div>
        {mobileMenuOpen && <>
          <button type="button" className="mobile-quick-menu-overlay" aria-label="Fermer le menu mobile" onClick={()=>{setMobileMenuOpen(false);setMobileMenuGroup(null)}} />
          <section className="mobile-quick-menu" aria-label="Menu mobile">
            <div className="mobile-quick-menu-head">
              <button type="button" className="mobile-quick-back" onClick={()=>mobileMenuGroup ? setMobileMenuGroup(null) : setMobileMenuOpen(false)}>{mobileMenuGroup ? '‹' : ''}</button>
              <div><strong>{mobileMenuGroup ? MENU_GROUP_LABELS[mobileMenuGroup] : 'Menu'}</strong><span>{mobileMenuGroup ? 'Choisir un écran' : 'Choisir une rubrique'}</span></div>
              <button type="button" className="mobile-quick-close" onClick={()=>{setMobileMenuOpen(false);setMobileMenuGroup(null)}}><span aria-hidden="true">×</span></button>
            </div>
            {!mobileMenuGroup ? <div className="mobile-quick-groups">
              {groupedNavItems.dashboard.map(item => { const Icon=item.icon; return <Link key={item.path} to={item.path} className="mobile-quick-group" onClick={()=>setMobileMenuOpen(false)}><Icon size={22}/><span>{item.label}</span></Link>; })}
              {mobileMenuGroups.map(group => { const Icon=group.icon; return <button key={group.key} type="button" className="mobile-quick-group" onClick={()=>setMobileMenuGroup(group.key)}><Icon size={22}/><span>{group.label}</span><em>{group.items.length}</em></button>; })}
            </div> : <div className="mobile-quick-links">
              {groupedNavItems[mobileMenuGroup].map(item => { const Icon=item.icon; const active=location.pathname===item.path || (item.path!=='/' && location.pathname.startsWith(item.path)); return <Link key={item.path} to={item.path} className={active?'active':''} onClick={()=>{setMobileMenuOpen(false);setMobileMenuGroup(null)}}><span className="mobile-quick-link-icon"><Icon size={19} weight={active?'fill':'regular'}/></span><span>{item.label}</span><CaretDown size={14} className="mobile-quick-link-arrow" /></Link>; })}
            </div>}
          </section>
        </>}

        <nav className={`mobile-bottom-nav ${mobileWorkspace.technicianMode ? 'technician-bottom-nav' : ''}`} aria-label="Navigation mobile / tablette">
          <Link to="/" className={location.pathname==='/'?'active':''}><House size={19}/><span>Accueil</span></Link>
          {mobileWorkspace.technicianMode ? <>
            {hasPermission('assets:read') && <Link to="/assets?scan=1" className={location.pathname.startsWith('/assets')&&location.search.includes('scan=1')?'active':''}><QrCode size={19}/><span>Scanner</span></Link>}
            {hasPermission('interventions:read') && <Link to="/interventions" className={location.pathname.startsWith('/interventions')?'active':''}><Wrench size={19}/><span>Interventions</span></Link>}
            {hasPermission('time:read') && <Link to="/time" className={location.pathname.startsWith('/time')?'active':''}><Clock size={19}/><span>Temps</span></Link>}
          </> : <>
            {hasPermission('projects:read') && <Link to="/projects" className={location.pathname.startsWith('/projects')?'active':''}><Folders size={19}/><span>Projets</span></Link>}
            {hasPermission('tasks:read') && <Link to="/tasks" className={location.pathname.startsWith('/tasks')?'active':''}><Target size={19}/><span>Tâches</span></Link>}
            {hasPermission('time:read') && <Link to="/time" className={location.pathname.startsWith('/time')?'active':''}><Clock size={19}/><span>Temps</span></Link>}
          </>}
          <button type="button" onClick={openMobileMenu} className={mobileMenuOpen ? 'active' : ''}><List size={19}/><span>Plus</span></button>
        </nav>
      </div>
    </div>
  );
}
